import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import type { Metric } from 'web-vitals';

import {
  BrowserMetrics,
  createSemanticMetrics,
  createUniversalReporter,
} from '@spotify-internal/semantic-metrics';
import type { WebVital } from '@spotify-internal/semantic-metrics';

const { sendMetric } = createSemanticMetrics({
  key: 'konfidens-standalone-web-app',
  reporter: createUniversalReporter(),
});

export { sendMetric };

// Routes that use plain-text names as IDs instead of base32-encoded IDs.
// Segments immediately after these are always generalized to `:id`.
const PLAIN_TEXT_ID_ROUTES = new Set(['flags', 'events']);

// Replaces dynamic path segments with `:id` so that web vitals metrics
// are grouped by route pattern instead of individual URLs.
export function generalizePathname(pathname: string): string {
  const segments = pathname.split('/');
  return segments
    .map((segment, i) => {
      if (!segment) return segment;
      // Generated resource IDs (AIP-122, 20 chars)
      if (/^[a-z][a-z0-9]{19}$/.test(segment)) {
        return ':id';
      }
      // MD5 hashes (32 hex chars)
      if (/^[0-9a-f]{32}$/.test(segment)) {
        return ':id';
      }
      // Plain-text IDs after known routes (e.g. /flags/my-flag-name)
      const prev = segments[i - 1];
      if (prev && PLAIN_TEXT_ID_ROUTES.has(prev)) {
        return ':id';
      }
      return segment;
    })
    .join('/');
}

function reportWebVitals(metric: Metric) {
  try {
    const webVital: WebVital = {
      id: metric.id,
      name: metric.name,
      label: metric.rating,
      value: metric.value,
    };
    sendMetric(
      BrowserMetrics.getWebVitalsMetric(webVital, {
        route: generalizePathname(window.location.pathname),
      }),
    );
  } catch {
    // ignore
  }
}

function observeLongTasks() {
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        sendMetric({
          what: 'long_task',
          metric_type: 'gauge',
          value: entry.duration,
          tags: { route: generalizePathname(window.location.pathname) },
        });
      }
    });
    observer.observe({ type: 'longtask', buffered: true });
  } catch {
    // ignore unsupported browsers (Firefox, Safari)
  }
}

export function setupWebVitals() {
  onCLS(reportWebVitals);
  onFCP(reportWebVitals);
  onINP(reportWebVitals);
  onLCP(reportWebVitals);
  onTTFB(reportWebVitals);
  observeLongTasks();
}
